import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useAppDispatch } from 'store';
import { createPDP, updatePDP, getPDP } from 'store/pdp/actions';
import { useResetAfterSubmit, usePrompt, useToast } from 'hooks';
import { commonTexts } from 'i18n';
import { CreatePDPRequest, PDPFormValues, UpdatePDPRequest } from 'types/pdp';

export interface UsePDPFormValuesArgs {
  pdpId?: number;
}

export default function usePDPFormValues(args: UsePDPFormValuesArgs = {}) {
  const { pdpId } = args;
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const showMessage = useToast();

  const form = useForm<PDPFormValues>({
    defaultValues: {
      name: '',
      description: '',
      reviewCycleId: null as any,
      careerAspiration: '',
      motivationFactors: [],
      currentEnergyLevel: 5,
      currentStrengthAndImprovements: '',
      supportRequired: '',
      developmentNeeds: [],
      requiredTrainings: [],
    },
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const { handleSubmit, reset, formState, getValues } = form;

  const [isSubmitting, setIsSubmitting] = useState(false);

  useResetAfterSubmit(form);
  usePrompt(formatMessage(commonTexts.unsavedChangesPrompt), formState.isDirty);

  const hydrate = useCallback(
    async (id: number) => {
      const resultAction = await dispatch(getPDP({ id }));
      if (getPDP.fulfilled.match(resultAction)) {
        const pdp = resultAction.payload;
        const values: PDPFormValues = {
          name: pdp.name,
          description: pdp.description || '',
          reviewCycleId: pdp.reviewCycleId,
          careerAspiration: pdp.careerAspiration || '',
          motivationFactors: pdp.motivationFactors || [],
          currentEnergyLevel: pdp.currentEnergyLevel || 5,
          currentStrengthAndImprovements:
            pdp.currentStrengthAndImprovements || '',
          supportRequired: pdp.supportRequired || '',
          developmentNeeds: pdp.developmentNeeds.map((need) => ({
            id: need.id,
            name: need.name,
            relatedArea: need.relatedArea,
            deadline: need.deadline,
            actionSteps: need.actionSteps.map((s) => ({
              id: s.id,
              name: s.name,
              isAchieved: s.isAchieved,
            })),
          })),
          requiredTrainings: pdp.requiredTrainings.map((t) => ({
            id: t.id,
            name: t.name,
            status: t.status,
          })),
        };
        reset(values, { keepDirty: false, keepTouched: false });
      }
    },
    [dispatch, reset],
  );

  const onSubmit = useCallback(async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      const formData = getValues();
      if (pdpId) {
        const updateData: UpdatePDPRequest = {
          name: formData.name,
          description: formData.description,
          reviewCycleId: formData.reviewCycleId,
          careerAspiration: formData.careerAspiration,
          motivationFactors: formData.motivationFactors,
          currentEnergyLevel: formData.currentEnergyLevel,
          currentStrengthAndImprovements:
            formData.currentStrengthAndImprovements,
          supportRequired: formData.supportRequired,
          developmentNeeds: formData.developmentNeeds.map((need) => {
            const needData: any = {
              name: need.name,
              relatedArea: need.relatedArea,
              deadline: need.deadline,
              actionSteps: need.actionSteps.map((step) => {
                const stepData: any = {
                  name: step.name,
                  isAchieved: step.isAchieved,
                };
                if (step.id !== undefined && step.id !== null) {
                  stepData.id = step.id;
                }
                return stepData;
              }),
            };
            if (need.id !== undefined && need.id !== null) {
              needData.id = need.id;
            }
            return needData;
          }),
          requiredTrainings: formData.requiredTrainings.map((training) => {
            const trainingData: any = {
              name: training.name,
              status: training.status,
            };
            if (training.id !== undefined && training.id !== null) {
              trainingData.id = training.id;
            }
            return trainingData;
          }),
        };
        await dispatch(updatePDP({ id: pdpId, data: updateData })).unwrap();
      } else {
        const createData: CreatePDPRequest = {
          name: formData.name,
          description: formData.description,
          reviewCycleId: formData.reviewCycleId,
          careerAspiration: formData.careerAspiration,
          motivationFactors: formData.motivationFactors,
          currentEnergyLevel: formData.currentEnergyLevel,
          currentStrengthAndImprovements:
            formData.currentStrengthAndImprovements,
          supportRequired: formData.supportRequired,
          developmentNeeds: formData.developmentNeeds.map((need) => ({
            name: need.name,
            relatedArea: need.relatedArea,
            deadline: need.deadline,
            actionSteps: need.actionSteps.map((step) => ({
              name: step.name,
              isAchieved: step.isAchieved,
            })),
          })),
          requiredTrainings: formData.requiredTrainings.map((training) => ({
            name: training.name,
            status: training.status,
          })),
        };
        await dispatch(createPDP({ data: createData })).unwrap();
      }
      showMessage({
        severity: 'success',
        message: formatMessage(commonTexts.changesSavedSuccessfully),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, getValues, pdpId, showMessage, formatMessage, isSubmitting]);

  const onSubmitClick = useMemo(
    () => handleSubmit(onSubmit),
    [handleSubmit, onSubmit],
  );

  const canSubmit = form.formState.isValid && form.formState.isDirty;

  return {
    form,
    onSubmitClick,
    isSubmitting,
    hydrate,
    canSubmit,
  };
}
